import type { AgentHost } from '../agent.js';
import type { Fetcher } from '../http.js';
import type { Clock, Ticker } from '../timing.js';

/**
 * Device health heartbeat. On an interval (default 15 min, all tiers) the
 * launcher POSTs a health snapshot to the licensing server, which stores the
 * time-series and bumps the device's last-seen for offline detection.
 *
 * Deliberately NOT built on the telemetry emitter: a heartbeat is a single
 * periodic snapshot where only the latest matters (staleness makes old beats
 * worthless), so the telemetry queue/persist/age-out machinery is a mismatch.
 * It's also not gated on the Enterprise `telemetryExport` entitlement — the
 * whole point is fleet liveness for every tier, including Core. Best-effort: a
 * failed beat is simply retried on the next tick, so there's no queue or
 * backoff to manage.
 */

export const DEFAULT_HEARTBEAT_INTERVAL_MS = 15 * 60_000;

/** The health signals a beat carries. Every field past tier is nullable —
 *  hosts report what they can (battery/signal are MDM-only). */
export interface HealthSnapshot {
  tier: string;
  online: boolean | null;
  batteryPct: number | null;
  batteryCharging: boolean | null;
  signalDbm: number | null;
  storageUsedBytes: number | null;
  storageTotalBytes: number | null;
  memoryUsedMb: number | null;
  memoryTotalMb: number | null;
  uptimeSec: number | null;
  appVersion: string | null;
  configVersion: string | null;
  /**
   * License status: 'valid' (a signed license verified), 'invalid' (present
   * but rejected → the launcher silently fell back to Core), 'none' (no license
   * at all). Lets the fleet view catch a silent downgrade the device can't
   * otherwise report (telemetry is Enterprise-gated).
   */
  licenseStatus: string | null;
  /**
   * The id of the license the device is running on, so a device flagged
   * `licenseStatus: invalid` links straight to its license row. Null for
   * legacy (no-id) licenses or Core.
   */
  licenseId: string | null;
}

/** Live context the collector reads at beat time (not at construction). */
export interface HealthContext {
  /** Effective tier the engine is honoring. */
  tier: () => string;
  /** Seconds since the launcher process started. */
  uptimeSec: () => number;
  /** Launcher build string, if known. */
  appVersion: string | null;
  /** Latest remote-config etag/version in effect, or null. */
  configVersion: () => string | null;
  /** 'valid' | 'invalid' | 'none', or null when unknown. */
  licenseStatus: string | null;
  /** The running license's id, or null. */
  licenseId: string | null;
}

const BYTES_PER_MB = 1024 * 1024;

/**
 * Build a health snapshot from the agent host's optional device/storage
 * probes plus the live engine context. Feature-detected: absent probes and
 * unreported fields yield null, never a throw.
 */
export async function collectHealthSnapshot(
  agentHost: Pick<AgentHost, 'getDeviceProperties' | 'getStorageInfo'>,
  ctx: HealthContext,
): Promise<HealthSnapshot> {
  const props = (await agentHost.getDeviceProperties?.().catch(() => undefined)) ?? undefined;
  const storage = (await agentHost.getStorageInfo?.().catch(() => undefined)) ?? undefined;

  // Storage: prefer the real device volume from StorageInfo.internal (native/
  // MDM hosts) over DeviceProperties' storage estimate — on an Android WebView
  // the latter is only the WebView's own origin quota, not the device. A plain
  // browser has no `internal`, so it falls back to the estimate as before.
  const internal = storage?.internal;
  const storageTotalBytes = internal?.totalSpace ?? props?.storageQuotaBytes ?? null;
  const storageUsedBytes =
    internal?.totalSpace !== undefined && internal.availableSpace !== undefined
      ? internal.totalSpace - internal.availableSpace
      : (props?.storageUsageBytes ?? null);

  // RAM: the MDM host reports total/available; a browser gives total GB only.
  const mem = storage?.memory;
  const memoryTotalMb =
    mem?.totalSpace !== undefined
      ? Math.round(mem.totalSpace / BYTES_PER_MB)
      : props?.deviceMemoryGb !== undefined
        ? Math.round(props.deviceMemoryGb * 1024)
        : null;
  const memoryUsedMb =
    mem?.totalSpace !== undefined && mem.availableSpace !== undefined
      ? Math.round((mem.totalSpace - mem.availableSpace) / BYTES_PER_MB)
      : null;

  return {
    tier: ctx.tier(),
    online: props?.online ?? null,
    // batteryLevel is 0–1; the wire wants a 0–100 percentage.
    batteryPct: props?.batteryLevel !== undefined ? Math.round(props.batteryLevel * 100) : null,
    batteryCharging: props?.batteryCharging ?? null,
    signalDbm: props?.signalDbm ?? null,
    storageUsedBytes,
    storageTotalBytes,
    memoryUsedMb,
    memoryTotalMb,
    uptimeSec: ctx.uptimeSec(),
    appVersion: ctx.appVersion,
    configVersion: ctx.configVersion(),
    licenseStatus: ctx.licenseStatus,
    licenseId: ctx.licenseId,
  };
}

export interface HeartbeatReporterOptions {
  /** Full heartbeat endpoint URL, e.g. https://api.mobilauncher.com/devices/heartbeat. */
  endpoint: string;
  deviceId: string;
  tenantId: string;
  collect: () => Promise<HealthSnapshot>;
  fetcher: Fetcher;
  ticker: Ticker;
  clock?: Clock;
  intervalMs?: number;
}

export class HeartbeatReporter {
  private cancelTicker: (() => void) | null = null;
  private beating = false;

  constructor(private readonly opts: HeartbeatReporterOptions) {}

  /** Send an immediate beat, then one every interval. Idempotent. */
  start(): void {
    if (this.cancelTicker) return;
    const interval = this.opts.intervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS;
    this.cancelTicker = this.opts.ticker.start(() => void this.beat(), interval);
    void this.beat(); // don't wait a full interval for the first liveness signal
  }

  stop(): void {
    if (this.cancelTicker) {
      this.cancelTicker();
      this.cancelTicker = null;
    }
  }

  /**
   * Collect + POST one snapshot. Best-effort: overlapping beats are skipped and
   * any failure is swallowed (the next tick retries). Exposed for tests.
   */
  async beat(): Promise<void> {
    if (this.beating) return;
    this.beating = true;
    try {
      const snapshot = await this.opts.collect();
      const reportedAt = Math.floor((this.opts.clock?.now().getTime() ?? Date.now()) / 1000);
      await this.opts.fetcher.fetch({
        url: this.opts.endpoint,
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          deviceId: this.opts.deviceId,
          tenantId: this.opts.tenantId,
          reportedAt,
          ...snapshot,
        }),
      });
    } catch {
      // Best-effort — the next tick tries again.
    } finally {
      this.beating = false;
    }
  }
}
